import React, { useEffect, useState } from "react";

export default function NotificationCenter() {
  const [statusMessage, setStatusMessage] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [logMessages, setLogMessages] = useState([]);

  useEffect(() => {
    // Event listener for status updates
    const handleStatusUpdate = (event) => {
      setStatusMessage(event.detail);
    };

    // Event listener for alerts
    const handleAlertTrigger = (event) => {
      setAlertMessage(event.detail);
    };

    // Event listener for log entries
    const handleLogEntry = (event) => {
      setLogMessages((prevMessages) => [...prevMessages, event.detail]);
    };

    // Registering event listeners
    window.addEventListener("statusUpdate", handleStatusUpdate);
    window.addEventListener("alertTriggered", handleAlertTrigger);
    window.addEventListener("logAdded", handleLogEntry);

    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener("statusUpdate", handleStatusUpdate);
      window.removeEventListener("alertTriggered", handleAlertTrigger);
      window.removeEventListener("logAdded", handleLogEntry);
    };
  }, []);

  return (
    <div className="sr-only">
      {statusMessage && (
        <div role="status" aria-live="polite" aria-atomic="true">
          {statusMessage}
        </div>
      )}
      {alertMessage && (
        <div role="alert" aria-live="assertive" aria-atomic="true">
          {alertMessage}
        </div>
      )}
      {logMessages && logMessages.length > 0 && (
        <div role="log" aria-live="polite" aria-atomic="false">
          {logMessages.map((msg, index) => <div key={index}>{msg}</div>)}
        </div>
      )}
    </div>
  );
}

// role="status": This role is for a live region
// containing advisory information for the user that is not important enough to be an alert.
// The screen reader will announce updates to this region at the next available opportunity,
// typically after finishing the current sentence.
// It has an implicit aria-live="polite" and aria-atomic="true".
function updateStatusMessage(message) {
  window.dispatchEvent(new CustomEvent("statusUpdate", { detail: message }));
}

// role="alert": This role is for important, and usually time-sensitive,
/// information. When this region is updated,
// screen readers will interrupt their current task to announce this update.
// It has an implicit aria-live="assertive" and aria-atomic="true".
function triggerAlertMessage(message) {
  window.dispatchEvent(new CustomEvent("alertTriggered", { detail: message }));
}
// role="log": This role is for a type of live region where new information
// is added in meaningful sequence and old information may disappear.
// Chat logs, carts for example, might use this role.
// The aria-live and aria-atomic attributes should be explicitly defined as needed.
function addLogEntry(message) {
  window.dispatchEvent(new CustomEvent("logAdded", { detail: message }));
}
export const a11y = {
  addLogEntry,
  triggerAlertMessage,
  updateStatusMessage
};
